import React, { useState, useRef } from 'react';
import { FormattedMessage } from 'react-intl';
import { Link } from 'react-router-dom';
import HelpIcon from '../../shared/HelpIcon';
import SvgOpenExternal from '../../svg/OpenExternal';
import Table from '../../shared/table/Table';
import {
  firstNameText,
  lastNameText,
  unitNameText,
} from '../../shared/SharedInternationalizations';
import InlineHelpText from '../../shared/InlineHelpText';
import permissions from '../../../permissionsList';
import AccessControl from '../../../AccessControl';

function UnitInfo(props) {
  const {
    id,
    address1,
    address2,
    city,
    state,
    zipcode,
    is_active,
  } = props.unitInfo;
  const { unitChildren, userList, currentLocation, parent_unit } = props;

  const quickEditChildUnitsText = (
    <FormattedMessage
      id="unitDisplay.quickEditChildUnitsText"
      defaultMessage="Quick Edit Child Units"
      description="A link to open the interface to edit the child units associated with this unit"
    />
  );

  const [showParentUnitHelpText, setShowParentUnitHelpText] = useState(false);

  const parentUnitHelpTextRef = useRef();
  function toggleParentHelpText(value) {
    setShowParentUnitHelpText(value);
  }

  const parentUnitHelpText = (
    <FormattedMessage
      id="unitDisplay.parentUnitHelpText"
      defaultMessage="This is the unit that is the next level higher in the hierarchy of units."
      description="Help text explaining what a parent unit is"
    />
  );

  const [showAssignedUsersHelpText, setShowAssignedUsersHelpText] = useState(
    false
  );
  const assignedUsersHelpTextRef = useRef();
  function toggleAssignedUsersHelpText(value) {
    setShowAssignedUsersHelpText(value);
  }

  const assignedUsersHelpText = (
    <FormattedMessage
      id="unitDisplay.assignedUsersHelpText"
      defaultMessage="These are users who have been assigned to this unit."
      description="Help text explaining who the assigned users are"
    />
  );
  const [showQuickEditUsersHelpText, setShowQuickEditUsersHelpText] = useState(
    false
  );
  const quickEditUsersHelpTextRef = useRef();
  function toggleQuickEditUsersHelpText(value) {
    setShowQuickEditUsersHelpText(value);
  }

  const quickEditUsersHelpText = (
    <FormattedMessage
      id="unitDisplay.quickEditUsersHelpText"
      defaultMessage="Clicking this button will take you to the interface to edit the users who are assigned to this unit. You can use this interface to remove a user or users from this unit or add a user or users to this unit."
      description="Help text for understanding what the quick edit user interface is"
    />
  );

  const quickEditUsersText = (
    <FormattedMessage
      id="unitDisplay.quickEditUsersText"
      defaultMessage="Quick Edit Users"
      description="A link to open the interface to add a child unit"
    />
  );
  const [showChildUnitsHelpText, setShowChildUnitsHelpText] = useState(false);
  const [
    showQuickEditChildUnitsHelpText,
    setShowQuickEditChildUnitsHelpText,
  ] = useState(false);

  const childUnitsHelpTextRef = useRef();
  function toggleChildUnitsHelpText(value) {
    setShowChildUnitsHelpText(value);
  }
  const childUnitsHelpText = (
    <FormattedMessage
      id="unitDisplay.childUnitsHelpText"
      defaultMessage="These are the units that have been assigned as children of this unit."
      description="Help text explaining what child units are"
    />
  );
  const quickEditChildUnitsHelpTextRef = useRef();
  function toggleQuickEditChildUnitsHelpText(value) {
    setShowQuickEditChildUnitsHelpText(value);
  }
  const quickEditChildUnitsHelpText = (
    <FormattedMessage
      id="unitDisplay.quickEditChildUnitsHelpText"
      defaultMessage="Clicking this button will take you to the interface where you can add or remove child units assigned to this unit."
      description="Help text for understanding what the quick edit child units interface is"
    />
  );

  const noChildUnitsText = (
    <FormattedMessage
      data-test="no-child-units-text"
      id="unitInfo.noChildUnitsText"
      defaultMessage="No Child Units Assigned"
      description="The user's contact phone number"
    />
  );

  const noUsersText = (
    <FormattedMessage
      id="unitInfo.noUsersText"
      defaultMessage="No Users Assigned"
      description="The user's contact phone number"
    />
  );

  const parentLink = parent_unit ? (
    <Link
      data-test="parent-unit-link"
      to={'/units/' + parent_unit.id + '/view/'}
      className=" margin-right-10 link-text align-center">
      {parent_unit.name}{' '}
      <SvgOpenExternal className="margin-left-5 text-small" />
    </Link>
  ) : (
    <span className="text-dimmed margin-right-10" data-test="no-parent-unit">
      <FormattedMessage
        data-test="no-parent-text"
        id="unitDisplay.noParentLabel"
        defaultMessage="No Parent Unit"
        description="Label indicating that there is no parent unit for the current unit"
      />
    </span>
  );

  function displayAddress() {
    let add1;
    let add2;
    let cityStateZipDisplay;

    if (address1) {
      add1 = (
        <div className="no-margin">
          <strong data-test="address1-display">{address1}</strong>
        </div>
      );
    }
    if (address2) {
      add2 = (
        <div className="no-margin">
          <strong data-test="address2-display">{address2}</strong>
        </div>
      );
    }
    if (city || state || zipcode) {
      let cityDisplay = '';
      let stateDisplay = '';
      let zipDisplay = '';
      if (city && state) {
        cityDisplay = `${city},`;
      } else if (city) {
        cityDisplay = `${city}`;
      }
      if (state) {
        stateDisplay = state;
      }
      if (zipcode) {
        zipDisplay = zipcode;
      }
      cityStateZipDisplay = (
        <div data-test="city-state-zip-display">
          <strong>
            {cityDisplay} {stateDisplay} {zipDisplay}
          </strong>
        </div>
      );
    }
    let address = (
      <React.Fragment>
        {add1}
        {add2}
        {cityStateZipDisplay}
      </React.Fragment>
    );
    if (address1 || address2 || city || state || zipcode) {
      return address;
    } else {
      return (
        <span className="text-dimmed margin-right-10">
          <FormattedMessage
            data-test="no-address"
            id="unitDevices.noAddressText"
            defaultMessage="No Address"
            description="The user's contact phone number"
          />
        </span>
      );
    }
  }

  /**
   * Function to generate the users table
   * @returns {object} - The table of users
   */
  const usersTable = () => {
    // map over the users and put into the format that is required for the table component
    const usersArray = userList.map((user) => {
      const userLink = (
        <FormattedMessage
          id="unitDisplay.userFullViewButton"
          defaultMessage="View User"
          description="Button to view full user info on user card component">
          {(txt) => (
            <div className="width-100 flex-end height-100 align-center">
              <Link
                data-test="user-link"
                to={`/users/${user.id}/view/`}
                className="text-small margin-right-10 link-text align-center">
                {txt} <SvgOpenExternal className="margin-left-5" />
              </Link>
            </div>
          )}
        </FormattedMessage>
      );
      return {
        firstName: {
          sortBy: user.first_name.toUpperCase(),
          display: `${user.first_name} ${
            user.preferred_name ? `"${user.preferred_name}"` : ''
          }`,
        },
        lastName: {
          sortBy: user.last_name.toUpperCase(),
          display: `${user.last_name} ${user.suffix ? `"${user.suffix}"` : ''}`,
        },
        fullView: {
          sortBy: '',
          display: userLink,
        },
      };
    });

    const tableHeadings = [
      {
        isSortable: true,
        sortBy: 'firstName',
        display: firstNameText,
      },
      {
        isSortable: true,
        sortBy: 'lastName',
        display: lastNameText,
      },
    ];
    // generates the actual table
    const table = (
      <Table
        hasTableHead={false}
        hasSelector={false}
        tableHeadings={tableHeadings}
        sortParam="lastName"
        tableData={usersArray}
        tableStyles="table-layout__3-column"
        data-test="users-table"
      />
    );
    if (userList.length > 0) {
      return table;
    } else {
      return (
        <span className="text-dimmed margin-right-10" data-test="no-users">
          {noUsersText}
        </span>
      );
    }
  };

  /**
   * Function to generate the units table
   * @returns {object} - The table of units
   */
  const unitsTable = () => {
    // map over the units and put into the format that is required for the table component
    if (unitChildren.length > 0) {
      const unitsArray = unitChildren.map((u) => {
        const unitLink = (
          <FormattedMessage
            id="unitDisplay.unitFullViewButton"
            defaultMessage="View Unit"
            description="Button to view full unit info">
            {(txt) => (
              <div className="width-100 flex-end height-100 align-center">
                <Link
                  data-test="child-unit-view-link"
                  to={`/units/${u.id}/view/`}
                  state={{
                    unit: id,
                    previous: currentLocation,
                  }}
                  className="text-small margin-right-10 link-text align-center">
                  {txt} <SvgOpenExternal className="margin-left-5" />
                </Link>
              </div>
            )}
          </FormattedMessage>
        );
        return {
          name: {
            sortBy: u.name.toUpperCase(),
            display: u.name,
          },
          fullView: {
            sortBy: '',
            display: unitLink,
          },
        };
      });
      // Take the array and filter based on the text entered in the search bar.

      const tableHeadings = [
        {
          isSortable: true,
          sortBy: 'name',
          display: unitNameText,
        },
      ];
      // generates the actual table
      const table = (
        <Table
          hasTableHead={false}
          hasSelector={false}
          tableHeadings={tableHeadings}
          sortParam="name"
          tableData={unitsArray}
          tableStyles="table__form-selector-table"
          data-test="units-table"
        />
      );
      return table;
    } else {
      return (
        <span
          data-test="no-child-units"
          className="text-dimmed margin-right-10">
          {noChildUnitsText}
        </span>
      );
    }
  };

  return (
    <div className="content-display__container" data-test="content-container">
      <div
        className="content-display__column content-display__column--1-2"
        data-test="address-container">
        <div className="content-display__item-pill-container">
          <div className="item-container__main-container">
            <div
              className="item-container__main-text text-large text-light"
              data-test="address-header">
              <FormattedMessage
                id="unitInfo.addressText"
                defaultMessage="Address"
                description="The user's contact phone number"
              />
            </div>
            <div
              className="item-container__secondary-text text-medium text-bold"
              data-test="address-block">
              {displayAddress()}
            </div>
          </div>
        </div>
      </div>
      <div
        className="content-display__column content-display__column--1-2"
        data-test="parent-unit-container">
        <div className="content-display__item-pill-container">
          <div className="item-container__main-container">
            <div className="item-container__main-text text-large text-light">
              <FormattedMessage
                id="unitDisplay.parent_unit"
                defaultMessage="Parent Unit"
                description="The parent unit for this unit"
              />
            </div>
            <div
              className="item-container__secondary-text text-medium text-bold"
              data-test="parent-unit-link-container">
              {parentLink}
            </div>
          </div>
          <div className="item-container__info-container">
            <HelpIcon
              onClick={() => parentUnitHelpTextRef.current.toggleHelpText()}
              toggled={showParentUnitHelpText}
              styles="margin-left-5"
            />
          </div>
        </div>
        <InlineHelpText
          ref={parentUnitHelpTextRef}
          toggleFunction={toggleParentHelpText}
          message={parentUnitHelpText}
        />
      </div>
      <div
        className="content-display__column"
        data-test="assigned-users-container">
        <div className="content-display__item-pill-container">
          <div className="item-container__main-container">
            <div className="item-container__main-text text-large text-light flex-spread margin-bottom-10">
              <div className="align-center">
                <FormattedMessage
                  id="unitDisplay.assignedUsersText"
                  defaultMessage="Assigned Users:"
                  description="The assigned users for this unit"
                />
                <HelpIcon
                  onClick={() =>
                    assignedUsersHelpTextRef.current.toggleHelpText()
                  }
                  toggled={showAssignedUsersHelpText}
                  styles="margin-left-5"
                />
              </div>
              <div className="align-center">
                <AccessControl
                  data-test="access-control"
                  feature={permissions.static.units.editUnit}>
                  {is_active ? (
                    <Link
                      data-test="associate-users-link"
                      className="link-button button-secondary button-xsm text-small link-secondary"
                      to={`/units/${id}/edit/?mode=associateUsers`}
                      state={{
                        modal: true,
                        previous: currentLocation,
                      }}>
                      {quickEditUsersText}
                    </Link>
                  ) : (
                    ''
                  )}
                </AccessControl>

                <HelpIcon
                  onClick={() =>
                    quickEditUsersHelpTextRef.current.toggleHelpText()
                  }
                  toggled={showQuickEditUsersHelpText}
                  styles="margin-left-5"
                />
              </div>
            </div>
            <div
              className="item-container__secondary-text text-medium text-bold"
              data-test="assigned-users-table">
              {usersTable()}
            </div>
          </div>
          <div className="item-container__info-container"></div>
        </div>
        <InlineHelpText
          ref={assignedUsersHelpTextRef}
          toggleFunction={toggleAssignedUsersHelpText}
          message={assignedUsersHelpText}
        />
        <InlineHelpText
          ref={quickEditUsersHelpTextRef}
          toggleFunction={toggleQuickEditUsersHelpText}
          message={quickEditUsersHelpText}
        />
      </div>
      <div
        className="content-display__column content-display__column--1-2"
        data-test="child-units-container">
        <div className="content-display__item-pill-container">
          <div className="item-container__main-container">
            <div className="item-container__main-text text-large text-light flex-spread margin-bottom-10">
              <div className="align-center">
                <FormattedMessage
                  id="unitDisplay.childUnitsText"
                  defaultMessage="Child Units:"
                  description="The child untis for this unit"
                />
                <HelpIcon
                  onClick={() => childUnitsHelpTextRef.current.toggleHelpText()}
                  toggled={showChildUnitsHelpText}
                  styles="margin-left-5"
                />
              </div>
              <div className="align-center">
                <AccessControl
                  data-test="access-control"
                  feature={permissions.static.units.editUnit}>
                  {is_active ? (
                    <Link
                      data-test="associate-child-units-link"
                      className="link-button button-secondary button-xsm text-small link-secondary"
                      to={`/units/${id}/edit/?mode=associateChildUnits`}
                      state={{
                        modal: true,
                        previous: currentLocation,
                      }}>
                      {quickEditChildUnitsText}
                    </Link>
                  ) : (
                    ''
                  )}
                </AccessControl>

                <HelpIcon
                  onClick={() =>
                    quickEditChildUnitsHelpTextRef.current.toggleHelpText()
                  }
                  toggled={showQuickEditChildUnitsHelpText}
                  styles="margin-left-5"
                />
              </div>
            </div>
            <div
              className="item-container__secondary-text text-medium text-bold"
              data-test="child-units-table">
              {unitsTable()}
            </div>
          </div>
          <div className="item-container__info-container"></div>
        </div>
        <InlineHelpText
          ref={childUnitsHelpTextRef}
          toggleFunction={toggleChildUnitsHelpText}
          message={childUnitsHelpText}
        />
        <InlineHelpText
          ref={quickEditChildUnitsHelpTextRef}
          toggleFunction={toggleQuickEditChildUnitsHelpText}
          message={quickEditChildUnitsHelpText}
        />
      </div>
    </div>
  );
}

export default UnitInfo;
